import * as React from "react";

import { Fieldset, Toggle, ToggleGroup } from "@/shared/foundation/primitives";

import { ParameterError } from "./ParameterError";
import {
  optionFor,
  unlessDefault,
  useParameterRecord,
  type SegmentedOption,
} from "./parameterValues";

export interface SegmentedParameterProps {
  name: string;
  parameterKey: string;
  title: string;
  hint?: string;
  options: readonly SegmentedOption[];
  /** The option the action uses when the key is absent. */
  defaultValue: string;
}

/**
 * One of a few values, as a row of toggles. Picking the default removes the
 * key, so the action's own default keeps applying.
 */
export function SegmentedParameter({
  name,
  parameterKey,
  title,
  hint,
  options,
  defaultValue,
}: SegmentedParameterProps) {
  const record = useParameterRecord(name);
  const legendId = React.useId();
  const stored = record.get(parameterKey);
  const current =
    optionFor(options, stored)?.value ??
    optionFor(options, defaultValue)?.value ??
    defaultValue;

  return (
    <Fieldset.Root data-testid={`param-${parameterKey}`}>
      <Fieldset.Legend id={legendId}>{title}</Fieldset.Legend>
      <ToggleGroup<string>
        value={[current]}
        onValueChange={(values) => {
          const [picked] = values;
          if (picked === undefined) {
            return;
          }
          record.write({
            [parameterKey]: unlessDefault(
              picked,
              optionFor(options, defaultValue)?.value ?? defaultValue,
            ),
          });
        }}
        aria-labelledby={legendId}
      >
        {options.map((option) => (
          <Toggle key={option.value} value={option.value}>
            {option.label}
          </Toggle>
        ))}
      </ToggleGroup>
      {hint !== undefined && (
        <Fieldset.Description>{hint}</Fieldset.Description>
      )}
      <ParameterError message={record.error(parameterKey)} />
    </Fieldset.Root>
  );
}
